import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Check, Trash2, KeyRound, Clock, User, DollarSign, Timer, BarChart3, AlertTriangle,
  Receipt, Download, Printer,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useFleetStore } from '@/stores/fleet';
import { rentalsApi, rentalStatus, type Rental, type RentalStatus } from '@/api/endpoints/rentals';
import { cn, formatDateTime } from '@/lib/utils';
import { BIPage } from '@/features/bi/BIPage';

const STATUS_META: Record<RentalStatus, { label: string; cls: string }> = {
  em_uso: { label: 'Em uso', cls: 'bg-status-running/10 text-status-running' },
  atrasada: { label: 'Atrasada', cls: 'bg-status-blocked/10 text-status-blocked' },
  finalizada: { label: 'Finalizada', cls: 'bg-status-off/15 text-status-off' },
};

type Periodo = 'dia' | 'semana' | 'mes' | 'ano';
const PERIODO_LABEL: Record<Periodo, string> = { dia: 'Hoje', semana: 'Semana', mes: 'Mês', ano: 'Ano' };

// Alerta quando faltam até 30 min para o retorno previsto.
const RETORNO_PROXIMO_MS = 30 * 60 * 1000;

function periodStart(p: Periodo): number {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  if (p === 'dia') return d.getTime();
  if (p === 'semana') {
    const wd = (d.getDay() + 6) % 7; // segunda = 0
    d.setDate(d.getDate() - wd);
    return d.getTime();
  }
  if (p === 'mes') {
    d.setDate(1);
    return d.getTime();
  }
  d.setMonth(0, 1); // ano
  return d.getTime();
}

/** Duração da locação em ms (até o retorno real, ou até agora se em uso). */
function rentalMs(r: Rental, nowMs: number): number {
  if (!r.saida_at) return 0;
  const start = new Date(r.saida_at).getTime();
  const end = r.retorno_real_at ? new Date(r.retorno_real_at).getTime() : nowMs;
  return Math.max(0, end - start);
}

function fmtBRL(v: number): string {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function fmtDuracao(ms: number): string {
  const totalMin = Math.round(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m}min`;
  return m === 0 ? `${h}h` : `${h}h ${m}min`;
}

type RentalTab = 'locacao' | 'bi';

export function RentalPage() {
  const query = useQuery({ queryKey: ['rentals'], queryFn: () => rentalsApi.list() });
  const records = query.data ?? [];
  const [creating, setCreating] = useState(false);
  const [periodo, setPeriodo] = useState<Periodo>('mes');
  const [tab, setTab] = useState<RentalTab>('locacao');

  const ativos = records.filter((r) => !r.finalizada);
  const now = Date.now();
  const atrasadas = ativos.filter((r) => rentalStatus(r) === 'atrasada');
  const proximas = ativos.filter((r) => {
    if (!r.retorno_previsto_at) return false;
    const t = new Date(r.retorno_previsto_at).getTime();
    return t > now && t - now <= RETORNO_PROXIMO_MS;
  });

  const stats = useMemo(() => {
    const start = periodStart(periodo);
    const inPeriod = records.filter((r) => r.saida_at && new Date(r.saida_at).getTime() >= start);
    const receita = inPeriod.reduce((s, r) => s + (r.valor ?? 0), 0);
    const horasMs = inPeriod.reduce((s, r) => s + rentalMs(r, now), 0);
    const agg = (keyOf: (r: Rental) => string) => {
      const m = new Map<string, { count: number; horasMs: number; receita: number }>();
      for (const r of inPeriod) {
        const cur = m.get(keyOf(r)) ?? { count: 0, horasMs: 0, receita: 0 };
        cur.count += 1;
        cur.horasMs += rentalMs(r, now);
        cur.receita += r.valor ?? 0;
        m.set(keyOf(r), cur);
      }
      return Array.from(m.entries()).map(([name, v]) => ({ name, ...v })).sort((a, b) => b.receita - a.receita);
    };
    const ranking = agg((r) => r.device_name).sort((a, b) => b.horasMs - a.horasMs);
    const porCliente = agg((r) => r.cliente || '—');
    const ticket = inPeriod.length ? receita / inPeriod.length : 0;
    return { count: inPeriod.length, receita, horasMs, ranking, porCliente, ticket, inPeriod };
  }, [records, periodo, now]);

  const exportBilling = () => {
    const head = ['embarcacao', 'cliente', 'saida', 'retorno_real', 'horas', 'valor'];
    const rows = stats.inPeriod.map((r) => [
      r.device_name, r.cliente,
      r.saida_at ?? '', r.retorno_real_at ?? '',
      (rentalMs(r, now) / 3600000).toFixed(2).replace('.', ','),
      (r.valor ?? 0).toFixed(2).replace('.', ','),
    ]);
    const csv = [head, ...rows].map((l) => l.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `faturamento-locadora-${periodo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex gap-1 border-b border-border px-4 pt-3 sm:px-6">
        <TabBtn active={tab === 'locacao'} onClick={() => setTab('locacao')} icon={KeyRound}>
          Locadora
        </TabBtn>
        <TabBtn active={tab === 'bi'} onClick={() => setTab('bi')} icon={BarChart3}>
          Painel Executivo
        </TabBtn>
      </div>
      {tab === 'bi' ? (
        <BIPage />
      ) : (
        <div className="space-y-4 p-4 sm:p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-xl font-bold">Locadora</h1>
              <p className="text-sm text-muted-foreground">Gestão de locações — clientes, faturamento, tempos e alertas.</p>
            </div>
            <Button onClick={() => setCreating(true)} className="gap-1.5"><Plus className="h-4 w-4" /> Nova locação</Button>
          </div>

      <div className="flex flex-wrap gap-2 text-[11px] font-bold">
        <span className="rounded-full bg-status-running/10 px-2 py-0.5 text-status-running">{ativos.length} em uso</span>
        <span className="rounded-full bg-status-blocked/10 px-2 py-0.5 text-status-blocked">{atrasadas.length} atrasadas</span>
        <span className="rounded-full bg-status-ready/10 px-2 py-0.5 text-status-ready">{proximas.length} perto do retorno</span>
      </div>

      {/* ===== GESTÃO (faturamento + tempos) ===== */}
      <section className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="flex items-center gap-2 text-sm font-bold"><BarChart3 className="h-4 w-4 text-brand" /> Gestão</h2>
          <div className="inline-flex rounded-full border border-border bg-card p-0.5 text-xs font-semibold">
            {(['dia', 'semana', 'mes', 'ano'] as Periodo[]).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPeriodo(p)}
                className={cn(
                  'rounded-full px-3 py-1 transition-colors',
                  periodo === p ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent',
                )}
              >
                {PERIODO_LABEL[p]}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard icon={KeyRound} label="Locações" value={String(stats.count)} sub={PERIODO_LABEL[periodo].toLowerCase()} />
          <StatCard icon={DollarSign} label="Receita" value={fmtBRL(stats.receita)} sub={PERIODO_LABEL[periodo].toLowerCase()} />
          <StatCard icon={Timer} label="Horas locadas" value={fmtDuracao(stats.horasMs)} sub={PERIODO_LABEL[periodo].toLowerCase()} />
          <StatCard icon={Receipt} label="Ticket médio" value={fmtBRL(stats.ticket)} sub="por locação" />
        </div>

        {(atrasadas.length > 0 || proximas.length > 0) && (
          <Card>
            <CardContent className="space-y-1 p-3 text-xs">
              {atrasadas.map((r) => (
                <p key={`a-${r.id}`} className="flex items-center gap-1.5 font-medium text-status-blocked">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  {r.device_name} · {r.cliente} — atrasada (previsto {formatDateTime(r.retorno_previsto_at!)})
                </p>
              ))}
              {proximas.map((r) => (
                <p key={`p-${r.id}`} className="flex items-center gap-1.5 font-medium text-status-ready">
                  <Clock className="h-3.5 w-3.5 shrink-0" />
                  {r.device_name} · {r.cliente} — retorno em breve ({formatDateTime(r.retorno_previsto_at!)})
                </p>
              ))}
            </CardContent>
          </Card>
        )}

        {stats.ranking.length > 0 && (
          <Card>
            <CardContent className="p-0">
              {/* rolagem horizontal: tabela larga nao pode empurrar a pagina inteira no celular */}
              <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="border-b border-border text-left text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-semibold">Embarcação</th>
                    <th className="px-3 py-2 text-right font-semibold">Locações</th>
                    <th className="px-3 py-2 text-right font-semibold">Tempo</th>
                    <th className="px-3 py-2 text-right font-semibold">Receita</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.ranking.map((d) => (
                    <tr key={d.name} className="border-b border-border/50 last:border-0">
                      <td className="px-3 py-2 font-medium">{d.name}</td>
                      <td className="px-3 py-2 text-right">{d.count}</td>
                      <td className="px-3 py-2 text-right">{fmtDuracao(d.horasMs)}</td>
                      <td className="px-3 py-2 text-right font-semibold">{fmtBRL(d.receita)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Faturamento por cliente + exportação */}
        {stats.porCliente.length > 0 && (
          <Card>
            <CardContent className="p-0">
              <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
                <p className="text-xs font-bold">Faturamento por cliente</p>
                <div className="flex gap-1.5">
                  <button type="button" onClick={exportBilling} className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] font-semibold hover:bg-accent">
                    <Download className="h-3 w-3" /> CSV
                  </button>
                  <button type="button" onClick={() => window.print()} className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] font-semibold hover:bg-accent">
                    <Printer className="h-3 w-3" /> PDF
                  </button>
                </div>
              </div>
              {/* rolagem horizontal: tabela larga nao pode empurrar a pagina inteira no celular */}
              <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="border-b border-border text-left text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-semibold">Cliente</th>
                    <th className="px-3 py-2 text-right font-semibold">Locações</th>
                    <th className="px-3 py-2 text-right font-semibold">Tempo</th>
                    <th className="px-3 py-2 text-right font-semibold">Receita</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.porCliente.map((c) => (
                    <tr key={c.name} className="border-b border-border/50 last:border-0">
                      <td className="px-3 py-2 font-medium">{c.name}</td>
                      <td className="px-3 py-2 text-right">{c.count}</td>
                      <td className="px-3 py-2 text-right">{fmtDuracao(c.horasMs)}</td>
                      <td className="px-3 py-2 text-right font-semibold">{fmtBRL(c.receita)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </CardContent>
          </Card>
        )}
      </section>

      {creating && <RentalEditor onClose={() => setCreating(false)} />}

      {query.isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando locações…</p>
      ) : query.error ? (
        <p className="text-sm text-destructive">Erro ao carregar (faça login).</p>
      ) : records.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
            <KeyRound className="h-10 w-10 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">Nenhuma locação registrada.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">{records.map((r) => <RentalCard key={r.id} record={r} />)}</div>
      )}
        </div>
      )}
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  icon: Icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof KeyRound;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-semibold transition-colors',
        active
          ? 'border-primary text-primary'
          : 'border-transparent text-muted-foreground hover:text-foreground',
      )}
    >
      <Icon className="h-4 w-4" /> {children}
    </button>
  );
}

function StatCard({ icon: Icon, label, value, sub }: { icon: typeof KeyRound; label: string; value: string; sub: string }) {
  return (
    <div className="rounded-xl bg-secondary/60 p-3">
      <div className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <p className="mt-1 text-base font-bold leading-tight">{value}</p>
      <p className="text-[11px] text-muted-foreground">{sub}</p>
    </div>
  );
}

function toLocalInput(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function RentalEditor({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const devices = useFleetStore((s) => s.devices);
  const sorted = useMemo(() => Array.from(devices.values()).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')), [devices]);

  const [deviceId, setDeviceId] = useState<number | ''>(sorted[0]?.id ?? '');
  const [cliente, setCliente] = useState('');
  const [telefone, setTelefone] = useState('');
  const [retorno, setRetorno] = useState(() => toLocalInput(Date.now() + 2 * 3600_000));
  const [valor, setValor] = useState('');
  const [obs, setObs] = useState('');
  const valid = deviceId !== '' && cliente.trim() && retorno;

  const save = useMutation({
    mutationFn: () => {
      const dev = devices.get(Number(deviceId));
      const valorNum = valor.trim() ? Number(valor.replace(',', '.')) : null;
      return rentalsApi.start({
        device_id: Number(deviceId), device_name: dev?.name ?? `Embarcação ${deviceId}`,
        cliente: cliente.trim(), telefone: telefone.trim() || null,
        retorno_previsto_at: new Date(retorno).toISOString(),
        valor: valorNum != null && Number.isFinite(valorNum) ? valorNum : null,
        obs: obs.trim() || null,
      });
    },
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: ['rentals'] }); onClose(); },
  });

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block space-y-1">
            <span className="text-xs font-semibold text-muted-foreground">Embarcação</span>
            <select value={deviceId} onChange={(e) => setDeviceId(e.target.value ? Number(e.target.value) : '')} className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm">
              {sorted.length === 0 && <option value="">Nenhuma embarcação</option>}
              {sorted.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </label>
          <label className="block space-y-1"><span className="text-xs font-semibold text-muted-foreground">Cliente</span>
            <Input value={cliente} onChange={(e) => setCliente(e.target.value)} placeholder="Nome do cliente" /></label>
          <label className="block space-y-1"><span className="text-xs font-semibold text-muted-foreground">Telefone</span>
            <Input value={telefone} onChange={(e) => setTelefone(e.target.value)} placeholder="(opcional)" /></label>
          <label className="block space-y-1"><span className="text-xs font-semibold text-muted-foreground">Retorno previsto</span>
            <Input type="datetime-local" value={retorno} onChange={(e) => setRetorno(e.target.value)} /></label>
          <label className="block space-y-1"><span className="text-xs font-semibold text-muted-foreground">Valor (R$)</span>
            <Input inputMode="decimal" value={valor} onChange={(e) => setValor(e.target.value)} placeholder="0,00 (opcional)" /></label>
        </div>
        <Input value={obs} onChange={(e) => setObs(e.target.value)} placeholder="Observação (área permitida, limite de velocidade…)" />
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => save.mutate()} disabled={!valid || save.isPending} className="gap-1.5"><Check className="h-4 w-4" /> {save.isPending ? 'Salvando…' : 'Iniciar locação'}</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function RentalCard({ record }: { record: Rental }) {
  const queryClient = useQueryClient();
  const st = rentalStatus(record);
  const meta = STATUS_META[st];
  const finish = useMutation({
    mutationFn: (valor?: number | null) => rentalsApi.finish(record.id, valor),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rentals'] }),
  });
  const del = useMutation({ mutationFn: () => rentalsApi.remove(record.id), onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rentals'] }) });

  const onFinish = () => {
    // Se ainda não há valor, pede o valor final cobrado (opcional).
    let valor: number | null | undefined = undefined;
    if (record.valor == null) {
      const v = window.prompt('Valor cobrado nesta locação (R$). Deixe em branco para pular:', '');
      if (v === null) return; // cancelou
      const n = Number(v.replace(',', '.'));
      valor = v.trim() && Number.isFinite(n) ? n : null;
    }
    finish.mutate(valor);
  };

  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate text-sm font-bold"><Link to={`/devices/${record.device_id}`} className="hover:underline">{record.device_name}</Link></p>
            <p className="flex items-center gap-1 text-xs text-muted-foreground"><User className="h-3.5 w-3.5" /> {record.cliente}{record.telefone && <span>· {record.telefone}</span>}</p>
          </div>
          <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold', meta.cls)}>{meta.label}</span>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
          {record.saida_at && <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Saída: {formatDateTime(record.saida_at)}</span>}
          {record.retorno_previsto_at && <span className={cn(st === 'atrasada' && 'font-bold text-status-blocked')}>Retorno previsto: {formatDateTime(record.retorno_previsto_at)}</span>}
          {record.retorno_real_at && <span>Devolvida: {formatDateTime(record.retorno_real_at)}</span>}
          {record.valor != null && <span className="flex items-center gap-1 font-semibold text-foreground"><DollarSign className="h-3.5 w-3.5" /> {fmtBRL(record.valor)}</span>}
        </div>
        {record.obs && <p className="text-xs italic text-foreground/80">"{record.obs}"</p>}
        <div className="flex justify-end gap-2 pt-1">
          {!record.finalizada && <Button size="sm" onClick={onFinish} disabled={finish.isPending} className="gap-1.5"><Check className="h-3.5 w-3.5" /> Finalizar</Button>}
          <button type="button" onClick={() => { if (window.confirm('Excluir esta locação?')) del.mutate(); }} className="rounded-md p-1.5 text-muted-foreground hover:bg-accent" aria-label="Excluir"><Trash2 className="h-4 w-4" /></button>
        </div>
      </CardContent>
    </Card>
  );
}
